import { ChevronDownPrimaryIcon, DownloadWhiteIcon, XBlackIcon } from '@assets/iconComponents';
import { Box, Button, Dialog, MenuItem, Select, styled } from '@mui/material';
import { AutosizeTextarea, Checkbox, LoadingSpinner, Text } from '..';
import { colorSet } from '@theme';
import { IImageGenerateResponse } from '@types';
import { useEffect, useRef, useState } from 'react';
import { usePostImageBgRemove, usePostImageGenerate } from '@api';
import { useToast } from '@hooks';
import { downloadImage } from '@utils';
import { ToastMessageEnum } from '@constant';

interface IProps {
  isVisible: boolean;
  onClose: () => void;
  isInitialPending: boolean;
  initialData?: IImageGenerateResponse;
  initialAiModel: string;
}

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const HistoryList = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  overflowY: 'auto',
  paddingRight: '4px',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  }
});

const SubPromptContainer = styled('div')({
  minHeight: '84px',
  maxHeight: '100%',
  flex: '0 1 auto',
  overflowY: 'auto',
  paddingRight: '4px',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  }
});

export const ImageGenerateModal = (props: IProps) => {
  const { isVisible, onClose, isInitialPending, initialData, initialAiModel } = props;

  const [history, setHistory] = useState<IImageGenerateResponse[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<IImageGenerateResponse>();
  const [mainPrompt, setMainPrompt] = useState<string>('');
  const [aiModel, setAiModel] = useState<string>(initialAiModel);
  const [isBgRemoveChecked, setIsBgRemoveChecked] = useState<boolean>(false);

  const { showToast } = useToast();

  const generateButtonContainerRef = useRef<HTMLDivElement>(null);

  const { mutateAsync, isPending } = usePostImageGenerate();

  const { mutateAsync: postImageBgRemoveMutateAsync, isPending: isPostImageBgRemovePending } = usePostImageBgRemove();

  const handleHistoryClick = (item: IImageGenerateResponse) => {
    setSelectedHistory(item);
  };

  const handleRegenerateButtonClick = async () => {
    // FormData 생성
    const formData = new FormData();

    // referenceImageKey 추가
    if (selectedHistory?.imageKey) {
      formData.append('referenceImageKey', selectedHistory?.imageKey);
    }

    // mainPrompt 추가
    formData.append('mainPrompt', mainPrompt);

    // 나노바나나 모델 추가
    formData.append('aiModel', aiModel);

    await mutateAsync({ body: formData }).then((res) => {
      const data = res.data.data;

      // 히스토리 및 선택된 이미지 업데이트
      setHistory((prev) => [data, ...prev]);
      setSelectedHistory(data);
      setMainPrompt('');
    });
  };

  const handleDownloadButtonClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation(); // 이벤트 전파 방지

    if (isBgRemoveChecked && selectedHistory?.imageKey) {
      await postImageBgRemoveMutateAsync({ imageKey: selectedHistory?.imageKey }).then((res) => {
        const imageS3Url = res.data.data.imageS3Url;
        downloadImage(imageS3Url).then((isSuccess) => {
          if (isSuccess) {
            showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
          }
        });
      });
    } else {
      downloadImage(selectedHistory?.imageS3Url).then((isSuccess) => {
        if (isSuccess) {
          showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
        }
      });
    }
  };

  useEffect(() => {
    if (initialData) {
      setHistory([initialData]);
      setSelectedHistory(initialData);
      setMainPrompt('');
    } else {
      setHistory([]);
      setSelectedHistory(undefined);
      setMainPrompt('');
    }
  }, [initialData]);

  // initialAiModel prop이 변경될 때마다 state 업데이트
  useEffect(() => {
    setAiModel(initialAiModel);
  }, [initialAiModel]);

  return (
    <>
      <Dialog
        open={isVisible}
        onClose={onClose}
        scroll="body"
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          },
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 'calc(100% - 597px)',
            height: 'calc(100% - 80px)',
            maxHeight: 'calc(100% - 80px)',
            margin: '40px 298.5px',
            padding: '60px 40px',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
            borderRadius: 0
          }
        }}
      >
        <XBlackIcon
          style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
          onClick={onClose}
        />

        <Text variant="h2" fontWeight="bold" marginBottom="30px">
          이미지 생성
        </Text>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            height: 'calc(100% - 62px)' // 부모 - 타이틀 영역
          }}
        >
          {/* 좌측 */}
          <div
            style={{
              height: '100%',
              padding: '10px',
              backgroundColor: colorSet.gray100,
              border: `1px solid ${colorSet.gray300}`,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {isInitialPending || history.length === 0 ? (
              <div
                style={{
                  width: 118,
                  height: 107
                }}
              />
            ) : (
              <HistoryList>
                {history.map((item, index) => {
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        handleHistoryClick(item);
                      }}
                      style={{
                        width: 118,
                        height: 107,
                        flexShrink: 0,
                        border:
                          selectedHistory && item.imageKey === selectedHistory.imageKey
                            ? `2px solid ${colorSet.primaryMain}`
                            : `1px solid ${colorSet.gray300}`,
                        cursor: 'pointer'
                      }}
                    >
                      <img src={item.imageS3Url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  );
                })}
              </HistoryList>
            )}
          </div>
          {/* 중앙 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: '100%',
                height: 'calc(100% - 77px)',
                backgroundColor: colorSet.gray200,
                border: `1px solid ${colorSet.gray400}`,
                marginBottom: '33px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {isInitialPending || isPending ? (
                <LoadingSpinner size={42} />
              ) : (
                <img
                  src={selectedHistory?.imageS3Url}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Checkbox
                checked={isBgRemoveChecked}
                onChange={(e) => {
                  setIsBgRemoveChecked(e.target.checked);
                }}
              />
              <Text variant="body1" marginRight="12px">
                배경 투명화
              </Text>
              <Button
                disabled={isInitialPending || isPending || !selectedHistory?.imageS3Url || isPostImageBgRemovePending}
                onClick={handleDownloadButtonClick}
                variant="contained"
                color="primary"
                sx={{ width: 240, height: 44 }}
              >
                <DownloadWhiteIcon style={{ marginRight: '10px' }} />
                다운로드
              </Button>
            </div>
          </div>
          {/* 우측 */}
          <div
            style={{
              width: '310px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Text variant="subtitle" fontWeight="bold" marginBottom="10px" style={{ flexShrink: 0 }}>
              보조 프롬프트
            </Text>
            <SubPromptContainer>
              <AutosizeTextarea
                defaultRow={3}
                placeholder="의상, 소품, 표정, 동작 등을 변경하세요."
                value={mainPrompt}
                onChange={(e) => {
                  setMainPrompt(e.target.value);
                }}
              />
            </SubPromptContainer>

            <Box
              ref={generateButtonContainerRef}
              sx={{ display: 'flex', alignItems: 'center', marginTop: '18px', flexShrink: 0 }}
            >
              <Select
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                renderValue={(value) => {
                  if (value === 'pro') return 'Pro';
                  if (value === '1') return '1.0';
                  return value;
                }}
                IconComponent={(props) => (
                  <ChevronDownPrimaryIcon
                    style={{
                      width: 14,
                      height: 14,
                      position: 'absolute',
                      right: '11px',
                      pointerEvents: 'none'
                    }}
                  />
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      width: generateButtonContainerRef.current?.offsetWidth || 'auto',
                      borderRadius: 0
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  }
                }}
                sx={{
                  width: '63px',
                  height: '44px',
                  cursor: 'pointer',
                  backgroundColor: colorSet.white,
                  color: colorSet.primaryMain,
                  border: `1px solid ${colorSet.primaryMain}`,
                  borderRadius: '0px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiSelect-select': {
                    padding: '0 8px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiSelect-icon': {
                    color: colorSet.primaryMain,
                    right: '4px'
                  }
                }}
              >
                <MenuItem
                  value="1"
                  sx={{
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                  }}
                >
                  나노바나나 1.0
                </MenuItem>
                <MenuItem
                  value="pro"
                  sx={{
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                  }}
                >
                  나노바나나 pro
                </MenuItem>
              </Select>
              <Button
                disabled={isInitialPending || isPending || mainPrompt.trim().length === 0}
                onClick={handleRegenerateButtonClick}
                variant="contained"
                sx={{
                  width: 'calc(100% - 63px)',
                  flexShrink: 0,
                  backgroundColor:
                    isInitialPending || isPending || mainPrompt.trim().length === 0
                      ? colorSet.gray300
                      : colorSet.gradientPrimaryMain,
                  color: colorSet.white
                }}
              >
                재생성
              </Button>
            </Box>
          </div>
        </div>
      </Dialog>
    </>
  );
};
